import type { ColumnDef, OnChangeFn, PaginationState } from "@tanstack/react-table";
import { Award, CheckCircle2, ChevronRight, ExternalLink, RotateCw, XCircle } from "lucide-react";
import { useMemo, useState } from "react";

import { DataTable } from "@/components/admin/DataTable";
import { ParticipationBadgesPanel } from "@/components/admin/ParticipationBadgesPanel";
import { SegmentedTabs, SegTab } from "@/components/admin/SegmentedTabs";
import { type BadgeTone, StatusBadge } from "@/components/admin/StatusBadge";
import { ErrorState, LoadingState } from "@/components/ui/data-states";
import { Input } from "@/components/ui/input";
import { useAdminScope } from "@/contexts/AdminScopeContext";
import {
  useApproveCertificateRun,
  useAuthoriseCertificateRun,
  useCertificateRun,
  useCertificateRunPreview,
  useCertificateSendLog,
  useCertificateTestSend,
  useCohortBadges,
  useCreateCertificateRun,
  useRetryCertificateEmail,
} from "@/hooks/admin/certificates";
import { AdminLayout } from "@/layouts/AdminLayout";
import type {
  CertificateEligibleRecipient,
  CertificateExcludedRecipient,
  CertificateExclusionReason,
  CertificateRun,
  CertificateRunPreview,
  CertificateSendLogItem,
} from "@/types";

/**
 * Certificates (KC-455): the gated per-cohort certificate send.
 *
 * Recipients tab is the dry run — the full query-built list with resolved
 * merge values, plus every exclusion with its reason. Send tab is the state
 * machine: test send to the client contact, mark the sign-off, then authorise
 * the live send (which stamps the cohort-wide issue date). Log tab is the
 * per-recipient outbox with retry on failures.
 *
 * A Dominion Free Cohort issues participation badges automatically instead
 * (KC-497), so it shows who has theirs rather than a run.
 */

const PAGE_SIZE = 25;

const REASON_LABELS: Record<CertificateExclusionReason, string> = {
  no_submissions: "No submissions",
  missing_email: "No email address",
  missing_name: "Missing full name",
  suppressed: "Email suppressed",
  already_sent: "Already sent",
};

/** What an admin can do about each exclusion — shown on the groups worth acting on. */
const REASON_HINTS: Partial<Record<CertificateExclusionReason, string>> = {
  missing_name: "Add a first and last name on the participant's account, then re-run the send.",
  missing_email: "No address on the profile — nothing to send to until one is set.",
  suppressed: "The address hard-bounced or complained. Remove it from the suppression list first.",
};

/**
 * Actionable exclusions first. `already_sent` and `no_submissions` are the two
 * that grow with the cohort and can't be acted on — they're the bulk of the list
 * and they hide the handful that need a fix.
 */
const REASON_ORDER: CertificateExclusionReason[] = [
  "missing_name",
  "missing_email",
  "suppressed",
  "already_sent",
  "no_submissions",
];

const COLLAPSED_REASONS: ReadonlySet<CertificateExclusionReason> = new Set([
  "already_sent",
  "no_submissions",
]);

const RUN_STATUS_LABELS: Record<CertificateRun["status"], string> = {
  draft: "Draft",
  test_sent: "Test sent",
  approved: "Signed off",
  authorised: "Authorised",
  completed: "Completed",
};

const RUN_STATUS_TONES: Record<CertificateRun["status"], BadgeTone> = {
  draft: "neutral",
  test_sent: "info",
  approved: "warning",
  authorised: "info",
  completed: "success",
};

const SEND_STATUS_TONES: Record<string, BadgeTone> = {
  planned: "info",
  sent: "success",
  failed: "danger",
  cancelled: "neutral",
  skipped: "neutral",
  suppressed: "warning",
};

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

// ---------------------------------------------------------------------------
// Recipients tab (the dry run)
// ---------------------------------------------------------------------------

function RecipientsTab({ preview }: { preview: CertificateRunPreview }) {
  const eligibleColumns: ColumnDef<CertificateEligibleRecipient>[] = [
    { accessorKey: "name", header: "name" },
    { accessorKey: "month", header: "month" },
    { accessorKey: "year", header: "year" },
    { accessorKey: "date", header: "date" },
    { accessorKey: "email", header: "Email" },
    {
      id: "certificate",
      header: "Certificate",
      cell: ({ row }) =>
        row.original.certificate_url ? (
          <a
            href={row.original.certificate_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 hover:underline"
          >
            View <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ) : (
          <span className="text-gray-400">Not issued yet</span>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <DataTable
        columns={eligibleColumns}
        data={preview.eligible}
        emptyMessage="No eligible recipients — participants qualify with one or more submissions."
        getRowId={(r) => r.user_programme_id}
        toolbar={
          <div className="text-sm text-gray-600">
            <span className="font-medium text-gray-900">{preview.eligible.length}</span> eligible ·{" "}
            <span className="font-medium text-gray-900">{preview.excluded.length}</span> excluded ·
            issue date is provisional until the send is authorised
          </div>
        }
      />
      {preview.excluded.length > 0 && <ExcludedPanel excluded={preview.excluded} />}
    </div>
  );
}

/**
 * Exclusions grouped by reason rather than one flat alphabetical list.
 *
 * In a mature cohort nearly every row is `already_sent` or `no_submissions`,
 * which buried the two or three people an admin can actually do something about.
 * Those two groups start collapsed; the actionable ones stay open with a note on
 * what to fix.
 */
function ExcludedPanel({ excluded }: { excluded: CertificateExcludedRecipient[] }) {
  const groups = useMemo(() => {
    const byReason = new Map<CertificateExclusionReason, CertificateExcludedRecipient[]>();
    for (const row of excluded) {
      const bucket = byReason.get(row.reason);
      if (bucket) bucket.push(row);
      else byReason.set(row.reason, [row]);
    }
    return REASON_ORDER.filter((reason) => byReason.has(reason)).map((reason) => ({
      reason,
      rows: byReason.get(reason) ?? [],
    }));
  }, [excluded]);

  const [expanded, setExpanded] = useState<ReadonlySet<CertificateExclusionReason>>(
    () => new Set(REASON_ORDER.filter((reason) => !COLLAPSED_REASONS.has(reason)))
  );

  const toggle = (reason: CertificateExclusionReason) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(reason)) next.delete(reason);
      else next.add(reason);
      return next;
    });

  const actionable = excluded.filter((r) => !COLLAPSED_REASONS.has(r.reason)).length;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <h3 className="mb-3 text-sm font-semibold text-amber-900">
        Excluded ({excluded.length}) — these participants will not receive a certificate
        {actionable > 0 && (
          <span className="ml-1 font-normal">
            · {actionable} need{actionable === 1 ? "s" : ""} a fix
          </span>
        )}
      </h3>
      <div className="flex flex-col gap-3">
        {groups.map(({ reason, rows }) => {
          const isOpen = expanded.has(reason);
          return (
            <div key={reason}>
              <button
                type="button"
                onClick={() => toggle(reason)}
                aria-expanded={isOpen}
                className="flex w-full items-center gap-2 text-left text-sm text-amber-900 hover:underline"
              >
                <ChevronRight
                  className={`h-4 w-4 flex-shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`}
                />
                <StatusBadge tone={COLLAPSED_REASONS.has(reason) ? "neutral" : "warning"}>
                  {REASON_LABELS[reason] ?? reason}
                </StatusBadge>
                <span className="font-medium">{rows.length}</span>
                {REASON_HINTS[reason] && (
                  <span className="font-normal text-amber-700">{REASON_HINTS[reason]}</span>
                )}
              </button>
              {isOpen && (
                <ul className="mt-1 space-y-1 pl-6">
                  {rows.map((r) => (
                    <li
                      key={r.user_programme_id}
                      className="flex items-center gap-2 text-sm text-amber-900"
                    >
                      <span>{r.name ?? "(no name)"}</span>
                      <span className="text-amber-700">{r.email ?? "(no email)"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Send tab (the state machine)
// ---------------------------------------------------------------------------

function ReadinessChecklist({ preview }: { preview: CertificateRunPreview }) {
  return (
    <ul className="space-y-2">
      {preview.readiness.items.map((item) => (
        <li key={item.key} className="flex items-start gap-2 text-sm">
          {item.passed ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
          ) : (
            <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          )}
          <span className={item.passed ? "text-gray-700" : "text-gray-900 font-medium"}>
            {item.label}
            {item.detail && <span className="block text-xs text-gray-500">{item.detail}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

function AuthoriseDialog({
  run,
  preview,
  onClose,
}: {
  run: CertificateRun;
  preview: CertificateRunPreview;
  onClose: () => void;
}) {
  const authorise = useAuthoriseCertificateRun();
  const [confirmText, setConfirmText] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const [issueDate, setIssueDate] = useState(run.issue_date ?? today);
  // A cohort has one issue date. Once it's stamped, a follow-up send inherits it
  // rather than letting late joiners carry a different date to the same cohort.
  const isRerun = ["authorised", "completed"].includes(run.status) && run.issue_date !== null;

  const canSend = preview.readiness.ready && confirmText === "SEND" && !authorise.isPending;

  const handleAuthorise = async () => {
    try {
      await authorise.mutateAsync({ runId: run.id, issueDate });
      onClose();
    } catch {
      // error surfaced below via authorise.error
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
        <h2 className="mb-1 text-lg font-semibold text-gray-900">
          {isRerun ? "Send to newly eligible participants" : "Authorise live send"}
        </h2>
        <p className="mb-4 text-sm text-gray-600">
          Emails {preview.eligible.length} participant
          {preview.eligible.length === 1 ? "" : "s"} their personal certificate link.{" "}
          {isRerun ? (
            <>
              Everyone already sent is skipped — this cannot resend to them. The{" "}
              {preview.excluded.length} excluded include those earlier recipients.
            </>
          ) : (
            <>
              {preview.excluded.length} excluded. This stamps the issue date on every certificate.
            </>
          )}
        </p>
        <div className="mb-4">
          <ReadinessChecklist preview={preview} />
        </div>
        {isRerun ? (
          <p className="mb-4 text-sm text-gray-600">
            Issue date <span className="font-medium text-gray-900">{formatDate(issueDate)}</span> —
            carried over so the whole cohort shares one date.
          </p>
        ) : (
          <label className="mb-4 block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Issue date (whole cohort)</span>
            <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
          </label>
        )}
        <label className="mb-4 block text-sm">
          <span className="mb-1 block font-medium text-gray-700">
            Type <span className="font-mono">SEND</span> to confirm
          </span>
          <Input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="SEND"
          />
        </label>
        {authorise.isError && (
          <p className="mb-3 text-sm text-red-600">
            {errorMessage(authorise.error, "Authorisation failed")}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSend}
            onClick={handleAuthorise}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {authorise.isPending ? "Sending…" : "Authorise live send"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SendTab({ run, preview }: { run: CertificateRun; preview: CertificateRunPreview }) {
  const testSend = useCertificateTestSend();
  const approve = useApproveCertificateRun();
  const [showAuthorise, setShowAuthorise] = useState(false);

  const testDone = run.test_sent_at !== null;
  const signedOff = ["approved", "authorised", "completed"].includes(run.status);
  const authorised = ["authorised", "completed"].includes(run.status);
  // After the first send, "eligible" means exactly the people a re-run would
  // reach: anyone already sent has moved into the excluded list.
  const newlyEligible = preview.eligible.length;
  const issueDateLabel = run.issue_date ? formatDate(run.issue_date) : "the date already stamped";

  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">Run status</span>
        <StatusBadge tone={RUN_STATUS_TONES[run.status]}>
          {RUN_STATUS_LABELS[run.status]}
        </StatusBadge>
        {run.issue_date && <span className="text-sm text-gray-600">· issued {run.issue_date}</span>}
      </div>

      {/* Step 1: test send */}
      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="mb-1 text-sm font-semibold text-gray-900">1 · Test send</h3>
        <p className="mb-3 text-sm text-gray-600">
          Sends the full merged email — with a real participant's certificate link — to the client
          contact for sign-off. Re-testing resets an earlier sign-off.
        </p>
        {testDone && (
          <p className="mb-3 text-sm text-gray-700">
            Last test sent {formatDateTime(run.test_sent_at)} to{" "}
            <span className="font-medium">{run.test_recipient_email}</span>
          </p>
        )}
        {testSend.isError && (
          <p className="mb-3 text-sm text-red-600">
            {errorMessage(testSend.error, "Test send failed")}
          </p>
        )}
        <button
          type="button"
          disabled={authorised || testSend.isPending || preview.eligible.length === 0}
          onClick={() => testSend.mutate({ runId: run.id })}
          className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {testSend.isPending ? "Sending…" : testDone ? "Send another test" : "Send test email"}
        </button>
      </section>

      {/* Step 2: sign-off */}
      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="mb-1 text-sm font-semibold text-gray-900">2 · Client sign-off</h3>
        <p className="mb-3 text-sm text-gray-600">
          Record here once the client has confirmed the test email and attachment look right. The
          live send stays locked until then.
        </p>
        {signedOff ? (
          <p className="text-sm text-green-700">Signed off {formatDateTime(run.approved_at)}</p>
        ) : (
          <>
            {approve.isError && (
              <p className="mb-3 text-sm text-red-600">
                {errorMessage(approve.error, "Could not record sign-off")}
              </p>
            )}
            <button
              type="button"
              disabled={run.status !== "test_sent" || approve.isPending}
              onClick={() => approve.mutate(run.id)}
              className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Mark test as signed off
            </button>
          </>
        )}
      </section>

      {/* Step 3: authorise */}
      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="mb-1 text-sm font-semibold text-gray-900">
          {authorised ? "3 · Send to newly eligible participants" : "3 · Authorise live send"}
        </h3>
        <p className="mb-3 text-sm text-gray-600">
          {authorised ? (
            <>
              The send stays open. Anyone who becomes eligible later — a participant who adds their
              full name, or whose first attempt failed — is picked up by the next run. Everyone
              already sent is skipped, and the issue date stays {issueDateLabel}.
            </>
          ) : (
            <>
              Stamps one issue date across the whole cohort, publishes every certificate page and
              emails each participant their personal link.
            </>
          )}
        </p>
        {authorised && (
          <p className="mb-3 text-sm text-green-700">
            Authorised {formatDateTime(run.authorised_at)} — {run.eligible_count} queued,{" "}
            {run.excluded_count} excluded
          </p>
        )}
        {authorised && newlyEligible === 0 ? (
          <p className="text-sm text-gray-500">
            Nobody is waiting — every eligible participant has their certificate.
          </p>
        ) : (
          <button
            type="button"
            disabled={!signedOff}
            onClick={() => setShowAuthorise(true)}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {authorised
              ? `Send to ${newlyEligible} new recipient${newlyEligible === 1 ? "" : "s"}`
              : "Authorise live send"}
          </button>
        )}
      </section>

      {showAuthorise && (
        <AuthoriseDialog run={run} preview={preview} onClose={() => setShowAuthorise(false)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Log tab
// ---------------------------------------------------------------------------

function LogTab({ runId }: { runId: string }) {
  const [page, setPage] = useState(1);
  const retry = useRetryCertificateEmail();
  const logQuery = useCertificateSendLog(runId, { page, page_size: PAGE_SIZE });

  const pagination: PaginationState = { pageIndex: page - 1, pageSize: PAGE_SIZE };
  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    setPage(next.pageIndex + 1);
  };

  const columns: ColumnDef<CertificateSendLogItem>[] = [
    { accessorKey: "name", header: "Recipient" },
    { accessorKey: "to_email", header: "Email" },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <StatusBadge tone={SEND_STATUS_TONES[row.original.status] ?? "neutral"}>
          {row.original.status}
        </StatusBadge>
      ),
    },
    {
      accessorKey: "sent_at",
      header: "Sent",
      cell: ({ row }) => formatDateTime(row.original.sent_at),
    },
    {
      accessorKey: "error_message",
      header: "Error",
      cell: ({ row }) => (
        <span className="text-xs text-gray-500">{row.original.error_message ?? "—"}</span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) =>
        row.original.status === "failed" ? (
          <button
            type="button"
            disabled={retry.isPending}
            onClick={() => retry.mutate(row.original.id)}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            <RotateCw className="h-3 w-3" /> Retry
          </button>
        ) : null,
    },
  ];

  const data = logQuery.data;
  return (
    <DataTable
      columns={columns}
      data={data?.items ?? []}
      isLoading={logQuery.isPending}
      error={logQuery.isError ? errorMessage(logQuery.error, "Failed to load send log") : null}
      onRetry={() => logQuery.refetch()}
      emptyMessage="Nothing sent yet — the log fills in once the send is authorised."
      getRowId={(r) => r.id}
      manualPagination
      pageCount={data?.total_pages ?? 0}
      rowCount={data?.total ?? 0}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
    />
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type TabId = "recipients" | "send" | "log";

export default function AdminCertificatesPage() {
  const { programmeId, defaultProgrammeId } = useAdminScope();
  const selectedProgrammeId = programmeId ?? defaultProgrammeId;
  const [tab, setTab] = useState<TabId>("recipients");

  const badgesQuery = useCohortBadges(selectedProgrammeId);
  const issuesBadges = badgesQuery.data?.credential === "participation_badge";
  const runQuery = useCertificateRun(
    badgesQuery.data?.credential === "certificate" ? selectedProgrammeId : null
  );
  const createRun = useCreateCertificateRun();
  const run = runQuery.data ?? null;
  const previewQuery = useCertificateRunPreview(run?.id);

  const body = useMemo(() => {
    if (!selectedProgrammeId) {
      return (
        <div className="flex h-full items-center justify-center text-sm text-gray-500">
          Pick a cohort in the topbar to manage its certificates.
        </div>
      );
    }
    if (badgesQuery.isPending) return <LoadingState className="h-full" />;
    if (badgesQuery.isError) {
      return (
        <ErrorState
          message={errorMessage(badgesQuery.error, "Failed to load the cohort")}
          onRetry={() => badgesQuery.refetch()}
        />
      );
    }
    if (issuesBadges)
      return <ParticipationBadgesPanel participants={badgesQuery.data.participants} />;
    if (runQuery.isPending) return <LoadingState className="h-full" />;
    if (runQuery.isError) {
      return (
        <ErrorState
          message={errorMessage(runQuery.error, "Failed to load certificate run")}
          onRetry={() => runQuery.refetch()}
        />
      );
    }
    if (!run) {
      return (
        <div className="mx-auto flex max-w-md flex-col items-center gap-4 pt-16 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
            <Award className="h-8 w-8 text-gray-400" />
          </div>
          <div>
            <h3 className="mb-1 text-lg font-medium text-gray-900">No certificate run yet</h3>
            <p className="text-sm text-gray-500">
              Starting a run builds the recipient list from submission data — nothing is sent until
              the test is signed off and the live send authorised.
            </p>
          </div>
          {createRun.isError && (
            <p className="text-sm text-red-600">
              {errorMessage(createRun.error, "Could not start the run")}
            </p>
          )}
          <button
            type="button"
            disabled={createRun.isPending}
            onClick={() => createRun.mutate(selectedProgrammeId)}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-40"
          >
            {createRun.isPending ? "Starting…" : "Start certificate run"}
          </button>
        </div>
      );
    }
    if (previewQuery.isPending) return <LoadingState className="h-full" />;
    if (previewQuery.isError || !previewQuery.data) {
      return (
        <ErrorState
          message={errorMessage(previewQuery.error, "Failed to load recipients")}
          onRetry={() => previewQuery.refetch()}
        />
      );
    }
    const preview = previewQuery.data;
    if (tab === "recipients") return <RecipientsTab preview={preview} />;
    if (tab === "send") return <SendTab run={preview.run} preview={preview} />;
    return <LogTab runId={run.id} />;
  }, [selectedProgrammeId, badgesQuery, issuesBadges, runQuery, run, createRun, previewQuery, tab]);

  return (
    <AdminLayout>
      <div className="flex h-full flex-col gap-4">
        {run && !issuesBadges && (
          <SegmentedTabs>
            <SegTab active={tab === "recipients"} onClick={() => setTab("recipients")}>
              Recipients
            </SegTab>
            <SegTab active={tab === "send"} onClick={() => setTab("send")}>
              Send
            </SegTab>
            <SegTab active={tab === "log"} onClick={() => setTab("log")}>
              Log
            </SegTab>
          </SegmentedTabs>
        )}
        <div className="min-h-0 flex-1">{body}</div>
      </div>
    </AdminLayout>
  );
}
